import React, { useState } from "react";
import { CheckCircle2, Database, Loader2, Plus, Sparkles } from "lucide-react";
import ActiveCardShell from "../ActiveCardShell";
import { sleep, RunButton, useAlive } from "../phase2/shared";

const FIELDS = [
  { label: "Liquor license #", type: "Text", value: "OH-LQ-448812" },
  { label: "Seasonal volume", type: "Currency", value: "$62,400 / summer" },
  { label: "Franchise group", type: "Picklist", value: "Coastal Eats Co." },
  { label: "Equipment lease end", type: "Date", value: "Mar 31, 2027" },
];

// Add bespoke fields and watch them drop into the live merchant record.
export default function CustomFieldsCard({ merchant }) {
  const [added, setAdded] = useState([]);
  const [busy, setBusy] = useState(false);
  const alive = useAlive();

  const add = (i) => setAdded((a) => (a.includes(i) ? a : [...a, i]));
  const run = async () => {
    setBusy(true); setAdded([]);
    for (let i = 0; i < FIELDS.length; i += 1) { await sleep(600); if (!alive.current) return; add(i); }
    setBusy(false);
  };

  return (
    <ActiveCardShell id="T7200cr" kicker="CUSTOM BUILD" title="Custom Data Fields" Icon={Database}
      actions={<RunButton tid="veroogle-t7200-run" running={busy} onClick={run} Icon={busy ? Loader2 : Sparkles} label="Add fields" />}>
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
        <div className="space-y-2">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">Field library</div>
          {FIELDS.map((f, i) => (
            <button key={f.label} type="button" onClick={() => add(i)} disabled={busy || added.includes(i)} data-testid={`veroogle-t7200-field-${i}`}
              className="w-full flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-slate-300 hover:border-brand-cyan/50 hover:text-white transition-colors disabled:opacity-35">
              <span className="inline-flex items-center gap-1.5"><Plus className="w-3 h-3" />{f.label}</span>
              <span className="font-mono text-[9px] uppercase text-slate-500">{f.type}</span>
            </button>
          ))}
        </div>
        <div className="rounded-xl border border-white/10 bg-navy-950/50 p-4" data-testid="veroogle-t7200-record">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-3">Merchant record · {merchant.name}</div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <Row k="Legal name" v={merchant.name} />
            <Row k="Monthly volume" v={`$${merchant.volume.toLocaleString()}`} />
            {added.map((i) => <Row key={i} k={FIELDS[i].label} v={FIELDS[i].value} fresh tid={`veroogle-t7200-row-${i}`} />)}
          </dl>
          {added.length === FIELDS.length && (
            <div data-testid="veroogle-t7200-outcome" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-navy-950 font-mono text-xs sm:text-sm font-bold tracking-[0.14em] uppercase shadow-[0_0_24px_rgba(16,185,129,0.4)] animate-in zoom-in-75 fade-in">
              <CheckCircle2 className="w-4 h-4" /> 4 custom fields live · 0 code
            </div>
          )}
        </div>
      </div>
    </ActiveCardShell>
  );
}

function Row({ k, v, fresh, tid }) {
  return (
    <div data-testid={tid} className={`rounded-md px-2 py-1.5 ${fresh ? "bg-emerald-500/10 border border-emerald-400/30 animate-in slide-in-from-left-2 fade-in duration-300" : "bg-white/[0.02]"}`}>
      <dt className="text-slate-500 text-[10px] uppercase tracking-wider">{k}</dt>
      <dd className="text-white truncate">{v}</dd>
    </div>
  );
}
